/**
 * NotificationPreferencesCard — #682. Which emails this customer gets, and
 * (for receptionist+) the switches to change them at the customer's request.
 *
 * The Counter Test it answers: a parent at the desk says "stop sending me
 * reminders", or "why am I not getting reminders?". One glance tells the
 * receptionist what is on, what is off and who set it; one tap and Save does
 * what the parent asked. Before this card, only a database write could.
 *
 * The consent rule is the server's (decision of record 2026-09-21): staff may
 * switch any email OFF, but may switch only booking, reminder and message
 * email back ON. Marketing is the customer's own opt-in. The card reads the
 * rule from the response (`staff_may_enable`) instead of restating it, and a
 * locked switch says why it is locked.
 *
 * DESIGN-PHILOSOPHY compliance:
 *  · P1 (one glance, one truth) — every row says On or Off in words. Reminders
 *    with booking emails off read "Off", because that is what the customer
 *    gets: reminders need both (#1044).
 *  · P2 (next action obvious) — one primary button (Save), disabled until
 *    something changed; Reset beside it.
 *  · P5 (never lie about data) — the provenance line says who set these, and
 *    only claims a staff name when the audit row proves the staff change is
 *    the current state. Otherwise it says "the customer or the Gingr import"
 *    rather than guessing. A failed load is an error with Retry, never an
 *    empty card that looks like the defaults.
 *  · Five states — loading (skeleton), error (with Retry), empty (the
 *    "standard settings, never changed" teaching line), default, saving.
 *    Stale does not apply: this is a live read of one row, not a mirror.
 *  · Tokens only.
 *
 * Texts and app notifications are not shown: no tenant sends texts (#529, the
 * customer app hides them too) and nothing delivers push. The API returns
 * every channel, so they can join this card when they reach a customer.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Alert, Button, Checkbox, Skeleton } from '@ferro/ui';
import {
  ApiError,
  fetchCustomerNotificationPreferences,
  humanizeApiError,
  updateCustomerNotificationPreferences,
  type CustomerNotificationPreferences,
  type CustomerNotificationPreferencesView,
  type NotificationChannel,
  type NotificationPreferencesProvenance,
} from '../../lib/api.js';
import './NotificationPreferencesCard.css';

export interface NotificationPreferencesCardProps {
  readonly apiUrl: string;
  readonly token: string;
  readonly customerId: string;
  /**
   * Whether this staffer holds `customers:write:all`. UX gate only: the
   * server is the authority and 403s regardless. Read-only roles (the
   * accountant) still see the settings and who set them.
   */
  readonly canWrite?: boolean;
}

type EmailChannel = Extract<
  NotificationChannel,
  'email_bookings' | 'email_reminders' | 'email_messages' | 'email_marketing'
>;

const EMAIL_ROWS: ReadonlyArray<{
  channel: EmailChannel;
  label: string;
  description: string;
  /** Rendered indented under its parent; off whenever the parent is off. */
  needs?: EmailChannel;
}> = [
  {
    channel: 'email_bookings',
    label: 'Booking emails',
    description: 'Confirmations and changes to their bookings.',
  },
  {
    channel: 'email_reminders',
    label: 'Visit reminders',
    description: 'The reminder email the day before a visit.',
    needs: 'email_bookings',
  },
  {
    channel: 'email_messages',
    label: 'Messages and report cards',
    description: 'When your team messages them or leaves a report card.',
  },
  {
    channel: 'email_marketing',
    label: 'News and offers',
    description: 'Promotions and announcements.',
  },
];

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; view: CustomerNotificationPreferencesView };

function messageOf(err: unknown): string {
  return humanizeApiError(err instanceof Error ? err.message : null);
}

/** Human timestamp. Raw ISO strings are an anti-pattern in this app. */
function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'on an unknown date';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function provenanceLine(p: NotificationPreferencesProvenance): string {
  switch (p.kind) {
    case 'staff':
      return `Last changed by ${p.staff_name} on ${formatWhen(p.changed_at)}.`;
    case 'customer_or_import':
      return `Set by the customer or imported from Gingr. Last changed ${formatWhen(p.changed_at)}.`;
    case 'defaults':
      return 'Standard settings. Nothing has been changed for this customer.';
    default: {
      const unhandled: never = p;
      return unhandled;
    }
  }
}

/** On/Off as the customer experiences it: a child is off when its parent is. */
function effectivelyOn(prefs: CustomerNotificationPreferences, row: (typeof EMAIL_ROWS)[number]): boolean {
  return prefs[row.channel] && (row.needs === undefined || prefs[row.needs]);
}

function changedChannels(
  draft: CustomerNotificationPreferences,
  saved: CustomerNotificationPreferences,
): EmailChannel[] {
  return EMAIL_ROWS.map((r) => r.channel).filter((c) => draft[c] !== saved[c]);
}

function labelOf(channel: EmailChannel): string {
  return EMAIL_ROWS.find((r) => r.channel === channel)?.label ?? channel;
}

export function NotificationPreferencesCard({
  apiUrl,
  token,
  customerId,
  canWrite = false,
}: NotificationPreferencesCardProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [draft, setDraft] = useState<CustomerNotificationPreferences | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedSummary, setSavedSummary] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const view = await fetchCustomerNotificationPreferences(apiUrl, token, customerId);
      setState({ kind: 'ready', view });
      setDraft(view.preferences);
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof ApiError ? err.message : messageOf(err),
      });
    }
  }, [apiUrl, token, customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (state.kind !== 'ready' || !draft || saving) return;
    const changed = changedChannels(draft, state.view.preferences);
    if (changed.length === 0) return;

    setSaving(true);
    setSaveError(null);
    setSavedSummary(null);
    const patch: Partial<CustomerNotificationPreferences> = {};
    for (const c of changed) patch[c] = draft[c];
    try {
      const view = await updateCustomerNotificationPreferences(apiUrl, token, customerId, patch);
      setState({ kind: 'ready', view });
      setDraft(view.preferences);
      setSavedSummary(
        `Saved. ${changed
          .map((c) => `${labelOf(c)} ${view.preferences[c] ? 'on' : 'off'}`)
          .join(', ')}.`,
      );
    } catch (err) {
      // Keep the form as the staffer left it. The server's own copy (the 422
      // consent refusal, the 409) is render-safe; anything else is humanized.
      setSaveError(err instanceof ApiError ? err.message : messageOf(err));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = (): void => {
    if (state.kind !== 'ready') return;
    setDraft(state.view.preferences);
    setSaveError(null);
    setSavedSummary(null);
  };

  return (
    <article
      className="ferro-dashboard__card"
      aria-label="Email notifications"
      data-testid="notification-preferences-card"
    >
      <header className="ferro-dashboard__cardHeader">
        <h2 className="ferro-dashboard__cardTitle">Email notifications</h2>
      </header>

      {state.kind === 'loading' ? (
        <div data-testid="notification-preferences-loading" aria-busy="true">
          <Skeleton variant="text" lines={4} />
        </div>
      ) : null}

      {state.kind === 'error' ? (
        <div data-testid="notification-preferences-error">
          <Alert variant="error">
            We couldn&apos;t load this customer&apos;s email settings. {state.message}
          </Alert>
          <div className="ferro-settings__actions">
            <Button type="button" variant="secondary" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        </div>
      ) : null}

      {state.kind === 'ready' && draft ? (
        <>
          <p
            className="ferro-muted ferro-notif-prefs__provenance"
            data-testid="notification-preferences-provenance"
          >
            {provenanceLine(state.view.provenance)}
          </p>

          {canWrite ? (
            <form className="ferro-form" onSubmit={handleSubmit} noValidate>
              {EMAIL_ROWS.map((row) => {
                const stored = state.view.preferences[row.channel];
                const lockedOff =
                  !stored && !state.view.staff_may_enable.includes(row.channel);
                const parentOff = row.needs !== undefined && !draft[row.needs];
                const description = lockedOff
                  ? 'Only the customer can turn this on, in their app.'
                  : parentOff
                    ? 'Not sent while booking emails are off.'
                    : row.description;
                return (
                  <div
                    key={row.channel}
                    className={
                      row.needs
                        ? 'ferro-notif-prefs__row ferro-notif-prefs__row--nested'
                        : 'ferro-notif-prefs__row'
                    }
                  >
                    <Checkbox
                      name={`notif-${row.channel}`}
                      label={row.label}
                      description={description}
                      checked={draft[row.channel]}
                      disabled={lockedOff || saving}
                      onChange={(e) => {
                        const next = e.target.checked;
                        setDraft((d) => (d ? { ...d, [row.channel]: next } : d));
                        setSavedSummary(null);
                      }}
                      data-testid={`notification-pref-${row.channel}`}
                    />
                  </div>
                );
              })}

              <div className="ferro-settings__actions">
                {/* The outcome lives INSIDE the actions bar, above the button
                    that caused it. Under 720px that bar is sticky, and an
                    alert placed above it in the flow ends up painted under it,
                    so the desk would tap Save and see nothing happen. */}
                {saveError ? (
                  <div
                    className="ferro-notif-prefs__outcome"
                    data-testid="notification-preferences-save-error"
                  >
                    <Alert variant="error">{saveError}</Alert>
                  </div>
                ) : null}
                {savedSummary && !saveError ? (
                  <div
                    className="ferro-notif-prefs__outcome"
                    data-testid="notification-preferences-save-success"
                  >
                    <Alert variant="success">{savedSummary}</Alert>
                  </div>
                ) : null}
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={saving}
                  disabled={
                    saving || changedChannels(draft, state.view.preferences).length === 0
                  }
                  data-testid="notification-preferences-save"
                >
                  Save
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleReset}
                  disabled={
                    saving || changedChannels(draft, state.view.preferences).length === 0
                  }
                  data-testid="notification-preferences-reset"
                >
                  Reset
                </Button>
              </div>
            </form>
          ) : (
            <dl className="ferro-settings__readonly" data-testid="notification-preferences-readonly">
              {EMAIL_ROWS.map((row) => {
                const on = effectivelyOn(state.view.preferences, row);
                const parentOff =
                  row.needs !== undefined && !state.view.preferences[row.needs];
                return (
                  <div
                    key={row.channel}
                    className={
                      row.needs
                        ? 'ferro-settings__readonly-row ferro-notif-prefs__row--nested'
                        : 'ferro-settings__readonly-row'
                    }
                  >
                    <dt>{row.label}</dt>
                    <dd data-testid={`notification-pref-value-${row.channel}`}>
                      <span
                        className={`ferro-profile-chip ferro-profile-chip--${on ? 'success' : 'neutral'}`}
                      >
                        {on ? 'On' : 'Off'}
                      </span>
                      {parentOff && state.view.preferences[row.channel] ? (
                        <span className="ferro-muted ferro-settings__readonly-hint">
                          Booking emails are off, so no reminders are sent.
                        </span>
                      ) : null}
                    </dd>
                  </div>
                );
              })}
            </dl>
          )}
        </>
      ) : null}
    </article>
  );
}
